import React, { useEffect, useMemo, useState } from "react";
import * as S from "./styles";
import { FaArrowLeft, FaHouse } from "react-icons/fa6";
import { Grid, Skeleton } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/services/api";
import { PlayerData } from "@/interfaces/liveGamesType";
import { Link } from "react-router-dom";
import RadarChart from "@/components/RadarChart";
import PlayerMetrics from "@/components/PlayerMetrics";
import { ITeam } from "@/interfaces/teamType";
import { translatePosition } from "@/utils/translatePositions";

export default function DetailedPlayer() {
  const params = useParams();
  const navigate = useNavigate();
  const [playerData, setPlayerData] = useState<PlayerData>({} as PlayerData);
  const [teamData, setTeamData] = useState<ITeam>({} as ITeam);
  const [loading, setLoading] = useState(true);
  const { playerId } = params;

  const goBack = () => {
    navigate(-1); // Navega para a rota anterior
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const resPlayer = await api.get(`/football/getPlayer?playerKey=${playerId}`);
        const playerData = resPlayer.data.data[0] as PlayerData;
        const resTeam = await api.get(`/football/getTeam?teamId=${playerData.team_key}`);
        const teamData = resTeam.data.data[0] as ITeam;
        setPlayerData(playerData);
        setTeamData(teamData);
      } catch (e) {
        console.error("Erro ao buscar dados do jogador:", e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [playerId]);

  const dribble_success_rate = useMemo(() => {
    const successes = parseInt(playerData.player_dribble_succ || '0');
    const attempts = parseInt(playerData.player_dribble_attempts || '0');
    return attempts > 0 ? (successes / attempts) * 100 : 0;
  }, [playerData.player_dribble_succ, playerData.player_dribble_attempts]);

  const duels_success_rate = useMemo(() => {
    const won = parseInt(playerData.player_duels_won || '0');
    const total = parseInt(playerData.player_duels_total || '0');
    return total > 0 ? (won / total) * 100 : 0;
  }, [playerData.player_duels_won, playerData.player_duels_total]);

  // Pesos para cálculos
  const weights = useMemo(() => ({
    DUELS_WEIGHT: 1,     // duels_success_rate já está em % (0-100)
    KEY_PASSES_WEIGHT: 5, // cada key pass vale mais pontos
    PASSES_WEIGHT: 0.1,   // cada passe vale menos, pois o total costuma ser alto
  }), []);

  // Dados de atributos do jogador para o gráfico radar
  const playerAttributes = useMemo(() => {
    const goals = parseInt(playerData.player_goals || '0');
    const assists = parseInt(playerData.player_assists || '0');
    const shots = parseInt(playerData.player_shots_total || '0');
    const keyPasses = parseInt(playerData.player_key_passes || '0');
    const crosses = parseInt(playerData.player_crosses_total || '0');
    const passesAccuracy = parseFloat(playerData.player_passes_accuracy || '0');
    const tackles = parseFloat(playerData.player_tackles || '0');
    const blocks = parseFloat(playerData.player_blocks || '0');
    const interceptions = parseFloat(playerData.player_interceptions || '0');
    const clearances = parseFloat(playerData.player_clearances || '0');
    const passes = parseInt(playerData.player_passes || '0');
   
    // Normalizar passesAccuracy dividindo por 10 para converter de formato "672" para 67.2%
    const normalizedPassesAccuracy = passesAccuracy / 10;
    
    const ataqueValue = parseFloat((goals * 5 + assists * 3 + shots * 1).toFixed());
    const criatividadeValue = parseFloat((keyPasses + crosses + assists * 2).toFixed());
    const tecnicaValue = parseFloat(((normalizedPassesAccuracy + dribble_success_rate) / 2).toFixed());
    const defesaValue = parseFloat(((tackles + blocks + interceptions + clearances) / 4).toFixed());
    const taticaValue = parseFloat(((duels_success_rate * weights.DUELS_WEIGHT + 
          keyPasses * weights.KEY_PASSES_WEIGHT + 
          passes * weights.PASSES_WEIGHT) / 3).toFixed());
    
    return {
      label1: ataqueValue,
      label2: criatividadeValue,
      label3: tecnicaValue,
      label4: defesaValue,
      label5: taticaValue,
    };
  }, [
    playerData.player_goals,
    playerData.player_assists,
    playerData.player_shots_total,
    playerData.player_key_passes,
    playerData.player_crosses_total,
    playerData.player_passes_accuracy,
    playerData.player_tackles,
    playerData.player_blocks,
    playerData.player_interceptions,
    playerData.player_clearances,
    playerData.player_passes,
    dribble_success_rate,
    duels_success_rate,
    weights
  ]);

  const playerMetrics = useMemo(() => [
    { label: "Gols", value: parseInt(playerData.player_goals || '0') },
    { label: "Assistências", value: parseInt(playerData.player_assists || '0') },
    { label: "Chutes totais", value: parseInt(playerData.player_shots_total || '0') },
    { label: "Dribles bem-sucedidos", value: parseInt(playerData.player_dribble_succ || '0') },
    { label: "Taxa de sucesso em dribles", value: Math.round(dribble_success_rate) },
    { label: "Passes totais", value: parseInt(playerData.player_passes || '0') },
    { label: "Precisão de passes", value: parseFloat(playerData.player_passes_accuracy || '0') },
    { label: "Passes-chave", value: parseInt(playerData.player_key_passes || '0') },
    { label: "Duelos vencidos", value: parseInt(playerData.player_duels_won || '0') },
    { label: "Taxa de duelos vencidos", value: Math.round(duels_success_rate) },
    { label: "Tackles", value: parseInt(playerData.player_tackles || '0') },
    { label: "Interceptações", value: parseInt(playerData.player_interceptions || '0') },
    { label: "Bloqueios", value: parseInt(playerData.player_blocks || '0') },
    { label: "Desarmes", value: parseInt(playerData.player_clearances || '0') },
  ], [playerData, dribble_success_rate, duels_success_rate]);

  return (
    <S.GlobalWrapper>
      <S.PageHeader>
        <S.HeaderContent>
          <S.BackButton onClick={goBack}>
            <FaArrowLeft /> Voltar
          </S.BackButton>
          
          <S.PageTitle>
            {/* Exibe um skeleton durante o carregamento ou o nome do jogador quando disponível */}
            {/* loading ? (
              <Skeleton variant="text" width={180} height={32} />
            ) : (
              <>{playerData.player_name || "Detalhes do Jogador"}</>
            ) */}
          </S.PageTitle>
          
          <Link to="/dashboard">
            <S.HomeButton>
              <FaHouse color="#fff"/>
            </S.HomeButton>
          </Link>
        </S.HeaderContent>
      </S.PageHeader>

      {/* Card do jogador principal */}
      <S.PlayerCard>
        {loading ? (
          <>
            <S.PlayerHeader>
              <Skeleton variant="circular" width={120} height={120} />
              <Skeleton variant="text" width={200} height={40} />
            </S.PlayerHeader>

            <S.PlayerInfoGrid>
              {[...Array(6)].map((_, index) => (
                <S.InfoItem key={index}>
                  <Skeleton variant="text" width={80} height={24} />
                  <Skeleton variant="text" width={100} height={24} />
                </S.InfoItem>
              ))}
            </S.PlayerInfoGrid>

            <S.ClubSection>
              <Skeleton variant="rectangular" width={60} height={60} />
              <S.ClubInfo>
                <Skeleton variant="text" width={150} height={30} />
              </S.ClubInfo>
            </S.ClubSection>
          </>
        ) : (
          <>
            <S.PlayerHeader>
              <img src={playerData.player_image} alt={playerData.player_name} />
              <h2>{playerData.player_name}</h2>
            </S.PlayerHeader>

            <S.PlayerInfoGrid>
              <S.InfoItem>
                <S.InfoLabel>Partidas Jogadas:</S.InfoLabel>
                <S.InfoValue>{playerData.player_match_played}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Nacionalidade:</S.InfoLabel>
                <S.InfoValue>
                  {playerData.player_country}
                </S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Idade:</S.InfoLabel>
                <S.InfoValue>{playerData.player_age}</S.InfoValue>
              </S.InfoItem>

              <S.InfoItem>
                <S.InfoLabel>Camisa:</S.InfoLabel>
                <S.InfoValue>{playerData.player_number}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Posição:</S.InfoLabel>
                <S.InfoValue>{translatePosition(playerData.player_type)}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Gols:</S.InfoLabel>
                <S.InfoValue>{playerData.player_goals}</S.InfoValue>
              </S.InfoItem>
            </S.PlayerInfoGrid>

            {/* Informações do clube */}
            <S.ClubSection>
              <S.ClubLogo src={teamData.team_badge} alt={teamData.team_name} />
              <S.ClubInfo>
                <h3>{teamData.team_name}</h3>
                {/* <p>Contrato até 30 jun 2025.</p> */}
              </S.ClubInfo>
            </S.ClubSection>
          </>
        )}
      </S.PlayerCard>

      {/* Seção de atributos do jogador */}
      <S.AttributesSection>
        <h3>Atributos do jogador</h3>
        {loading ? (
          <S.RadarChartContainer style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Skeleton variant="circular" width={200} height={200} />
          </S.RadarChartContainer>
        ) : (
          <S.RadarChartContainer>
            <RadarChart
              attributes={playerAttributes}
              labels={["Ataque", "Criativ.", "Técnica", "Defesa", "Tática"]}
            />
          </S.RadarChartContainer>
        )}
        <S.AttributesFooter>
          {/* Atributos atualizados em set de 2023 */}
        </S.AttributesFooter>
      </S.AttributesSection>

      <PlayerMetrics metrics={playerMetrics} loading={loading} />
    </S.GlobalWrapper>
  );
}
